"use client";

import { useCallback, useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";

interface ArticleImageProps {
  src: string;
  alt?: string;
  dialogLabel?: string;
  closeLabel?: string;
}

/**
 * Inline article image rendered by react-markdown. Click opens a full-size
 * lightbox overlay; close by clicking the backdrop or pressing Esc.
 */
export function ArticleImage({
  src,
  alt = "",
  dialogLabel = "Article image",
  closeLabel = "Close",
}: ArticleImageProps) {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [open, close]);

  return (
    <>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={alt}
        onClick={() => setOpen(true)}
        className="cursor-zoom-in"
      />
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={alt || dialogLabel}
          onClick={close}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-[16px] cursor-zoom-out max-tablet:p-[8px]"
        >
          <button
            type="button"
            onClick={close}
            aria-label={closeLabel}
            className="absolute top-[16px] right-[16px] flex size-[44px] items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors max-tablet:top-[8px] max-tablet:right-[8px] max-tablet:size-[40px]"
          >
            <IoClose className="size-[28px]" />
          </button>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-full rounded-[12px] object-contain cursor-default"
          />
        </div>
      )}
    </>
  );
}
